// File: src/components/common/Navbar.jsx

import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Bars3Icon,
    XMarkIcon,
    ChevronDownIcon,
    PhoneIcon,
    LanguageIcon
} from '@heroicons/react/24/outline';

import { useLanguage } from '../../context/LanguageContext';
import { CONTACT_INFO } from '../../utils/constants';

// ─────────────────────────────────────────────
// Custom Dropdown Component (replaces Headless UI Menu)
// Handles open/close + click outside automatically
// ─────────────────────────────────────────────
const CustomDropdown = ({ trigger, children, menuClassName = '' }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    // Close when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (ref.current && !ref.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <div className="nav-dropdown relative" ref={ref}>
            {/* Render trigger button, pass open state and toggle fn */}
            {trigger({ open, toggle: () => setOpen(prev => !prev) })}

            {/* Dropdown menu with animation */}
            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, y: -8, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -8, scale: 0.95 }}
                        transition={{ duration: 0.15 }}
                        className={`dropdown-menu glass-effect shadow-medium ${menuClassName}`}
                    >
                        {/* Pass close fn to children */}
                        {children({ close: () => setOpen(false) })}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

// ─────────────────────────────────────────────
// Main Navbar Component
// ─────────────────────────────────────────────
const Navbar = () => {
    const [isScrolled, setIsScrolled] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    const { currentLanguage, toggleLanguage, getText, LANGUAGES } = useLanguage();

    // Handle scroll effect
    useEffect(() => {
        const handleScroll = () => setIsScrolled(window.scrollY > 20);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Close mobile menu when route changes
    useEffect(() => {
        setIsMobileMenuOpen(false);
    }, [location]);

    // Navigation items
    const navigationItems = [
        { path: '/', label: { [LANGUAGES.EN]: 'Home', [LANGUAGES.MM]: 'ပင်မ' } },
        {
            path: '/academic',
            label: { [LANGUAGES.EN]: 'Academic', [LANGUAGES.MM]: 'တက္ကသိုလ်' },
            children: [
                { path: '/main-faculties', label: { [LANGUAGES.EN]: 'University Board', [LANGUAGES.MM]: 'မဟာနာယကဆရာတော်များ' } },
                { path: '/registrar', label: { [LANGUAGES.EN]: 'Registrar Department', [LANGUAGES.MM]: 'မော်ကွန်းထိန်းဌာန' } },
                { path: '/offering-degrees', label: { [LANGUAGES.EN]: 'Offering Degrees', [LANGUAGES.MM]: 'သင်ကြားနိင်သောဘွဲ့များ' } },
                { path: '/current-activities', label: { [LANGUAGES.EN]: 'Current Activities', [LANGUAGES.MM]: 'လှုပ်ရှားမှု' } },
                { path: '/faculties', label: { [LANGUAGES.EN]: 'Faculties', [LANGUAGES.MM]: 'မဟာဌာနများ' } },
                { path: '/notices', label: { [LANGUAGES.EN]: 'Notices & Informations', [LANGUAGES.MM]: 'ကြေငြာချက်များနှင့် သတင်းအချက်အလက်များ' } }
            ]
        },
        {
            path: '/foundation',
            label: { [LANGUAGES.EN]: 'Foundation', [LANGUAGES.MM]: 'ဖောင်ဒေးရှင်း' },
            children: [
                { path: '/members', label: { [LANGUAGES.EN]: 'Members', [LANGUAGES.MM]: 'အဖွဲ့ဝင်များ' } },
                { path: '/foundation-activities', label: { [LANGUAGES.EN]: 'Activities', [LANGUAGES.MM]: 'လှုပ်ရှားမှုများ' } },
                { path: '/donation', label: { [LANGUAGES.EN]: 'Donation', [LANGUAGES.MM]: 'အလှူငွေ' } }
            ]
        },
        { path: '/about', label: { [LANGUAGES.EN]: 'About', [LANGUAGES.MM]: 'တက္ကသိုလ်အကြောင်း' } },
        { path: '/gallery', label: { [LANGUAGES.EN]: 'Gallery', [LANGUAGES.MM]: 'မှတ်တမ်း' } },
        { path: '/library', label: { [LANGUAGES.EN]: 'Library', [LANGUAGES.MM]: 'စာကြည့်တိုက်' } },
        { path: '/reading-room', label: { [LANGUAGES.EN]: 'Reading Room', [LANGUAGES.MM]: 'စာဖတ်ခန်း' } }
    ];

    const isActiveNavItem = (navItem) => {
        if (navItem.path === '/' && location.pathname === '/') return true;
        if (navItem.path !== '/' && location.pathname.startsWith(navItem.path)) return true;
        if (navItem.children) {
            return navItem.children.some(child => location.pathname === child.path);
        }
        return false;
    };

    return (
        <>
            {/* ── Top Bar ── */}
            <div className="navbar-topbar">
                <div className="container d-flex justify-between align-center">
                    <a href="tel:09667654455" className="topbar-contact hover-underline">
                        <PhoneIcon className="topbar-icon" />
                        <span className="topbar-phone">09 667 654455</span>
                    </a>
                    <span className={`topbar-welcome ${currentLanguage === LANGUAGES.MM ? 'myanmar-text' : ''}`}>
                        {getText({
                            [LANGUAGES.EN]: 'Welcome to Mahavihara Dhamma Vinaya University',
                            [LANGUAGES.MM]: 'မဟာဝိဟာရဓမ္မဝိနယတက္ကသိုလ်က ကြိုဆိုပါသည်'
                        })}
                    </span>
                </div>
            </div>

            {/* ── Main Navigation ── */}
            <nav className={`navbar-main sticky z-50 ${isScrolled ? 'navbar-scrolled glass-effect shadow-medium' : 'shadow-soft'}`}>
                <div className="container">
                    <div className="navbar-content d-flex justify-between align-center">

                        {/* Brand / Logo */}
                        <Link to="/" className="navbar-brand d-flex align-center gap-2 hover-scale-small">
                            <div className="logo-container relative">
                                <img src="/logo.png" alt="Mahavihara University Logo" className="navbar-logo-img" />
                                <div className="logo-glow absolute"></div>
                            </div>
                            <div className="brand-text d-flex flex-column">
                                <h1 className={`brand-title ${currentLanguage === LANGUAGES.MM ? 'myanmar-text' : ''}`}>
                                    {getText({ [LANGUAGES.EN]: 'Mahavihara', [LANGUAGES.MM]: 'မဟာဝိဟာရ' })}
                                </h1>
                                <p className={`brand-subtitle ${currentLanguage === LANGUAGES.MM ? 'myanmar-text' : ''}`}>
                                    {getText({ [LANGUAGES.EN]: 'Dhamma Vinaya University', [LANGUAGES.MM]: 'ဓမ္မဝိနယတက္ကသိုလ်' })}
                                </p>
                            </div>
                        </Link>

                        {/* Desktop Nav */}
                        <div className="navbar-actions d-flex align-center gap-2">
                            <div className="navbar-nav d-flex align-center gap-1">
                                {navigationItems.map((navItem) => (
                                    <div key={navItem.path} className="nav-item relative">
                                        {navItem.children ? (
                                            // ✅ Custom dropdown — full control over open/close
                                            <CustomDropdown
                                                trigger={({ open, toggle }) => (
                                                    <button
                                                        onClick={toggle}
                                                        className={`nav-link nav-dropdown-toggle d-flex align-center gap-1 hover-underline ${currentLanguage === LANGUAGES.MM ? 'myanmar-text' : ''} ${isActiveNavItem(navItem) ? 'nav-link-active' : ''}`}
                                                    >
                                                        <span>{getText(navItem.label)}</span>
                                                        <ChevronDownIcon className={`dropdown-icon ${open ? 'dropdown-icon-open' : ''}`} />
                                                    </button>
                                                )}
                                            >
                                                {({ close }) => (
                                                    <>
                                                        {/* Dropdown header */}
                                                        <div className="dropdown-header">
                                                            <p className={`dropdown-title ${currentLanguage === LANGUAGES.MM ? 'myanmar-text' : ''}`}>
                                                                {getText(navItem.label)}
                                                            </p>
                                                        </div>
                                                        {/* Dropdown items — close() on click */}
                                                        {navItem.children.map((child) => (
                                                            <Link
                                                                key={child.path}
                                                                to={child.path}
                                                                onClick={close} // ✅ guaranteed close
                                                                className={`dropdown-item hover-underline hover-scale-small ${currentLanguage === LANGUAGES.MM ? 'myanmar-text' : ''} ${location.pathname === child.path ? 'dropdown-item-current' : ''}`}
                                                            >
                                                                {getText(child.label)}
                                                            </Link>
                                                        ))}
                                                    </>
                                                )}
                                            </CustomDropdown>
                                        ) : (
                                            <Link
                                                to={navItem.path}
                                                className={`nav-link hover-underline ${currentLanguage === LANGUAGES.MM ? 'myanmar-text' : ''} ${isActiveNavItem(navItem) ? 'nav-link-active' : ''}`}
                                            >
                                                {getText(navItem.label)}
                                            </Link>
                                        )}
                                    </div>
                                ))}
                            </div>

                            {/* ✅ Language Switcher — also custom dropdown */}
                            <CustomDropdown
                                menuClassName="dropdown-menu-lang"
                                trigger={({ open, toggle }) => (
                                    <button
                                        onClick={toggle}
                                        className="lang-button hover-scale-small"
                                        title="Select Language"
                                    >
                                        <LanguageIcon className="lang-icon" />
                                        <span className="lang-code">{currentLanguage === LANGUAGES.EN ? 'EN' : 'MM'}</span>
                                        <ChevronDownIcon className={`lang-chevron ${open ? 'lang-chevron-open' : ''}`} />
                                    </button>
                                )}
                            >
                                {({ close }) => (
                                    <>
                                        {/* English option */}
                                        <button
                                            onClick={() => { toggleLanguage(); close(); }} // ✅ close after toggle
                                            className={`dropdown-item dropdown-item-lang ${currentLanguage === LANGUAGES.EN ? 'dropdown-item-current' : ''}`}
                                        >
                                            <span className="lang-flag">🇬🇧 </span>
                                            <span className="lang-name">English</span>
                                            {currentLanguage === LANGUAGES.EN && <span className="lang-check">✓</span>}
                                        </button>
                                        {/* Myanmar option */}
                                        <button
                                            onClick={() => { toggleLanguage(); close(); }} // ✅ close after toggle
                                            className={`dropdown-item dropdown-item-lang myanmar-text ${currentLanguage === LANGUAGES.MM ? 'dropdown-item-current' : ''}`}
                                        >
                                            <span className="lang-flag">🇲🇲 </span>
                                            <span className="lang-name">မြန်မာ</span>
                                            {currentLanguage === LANGUAGES.MM && <span className="lang-check">✓</span>}
                                        </button>
                                    </>
                                )}
                            </CustomDropdown>
                        </div>

                        {/* Mobile Toggle Button */}
                        {/* Mobile Right Controls — lang button + hamburger */}
<div className="mobile-controls d-flex align-center gap-1">
    {/* Quick language toggle — visible only on mobile */}
    <button
        onClick={toggleLanguage}
        className="mobile-lang-quick-btn"
        title="Toggle Language"
    >
        <LanguageIcon className="lang-icon" />
        <span className="lang-code">
            {currentLanguage === LANGUAGES.EN ? 'EN' : 'MM'}
        </span>
    </button>

    {/* Hamburger */}
    <button
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        className="mobile-nav-toggle hover-scale ripple"
    >
        {isMobileMenuOpen
            ? <XMarkIcon className="mobile-nav-icon" />
            : <Bars3Icon className="mobile-nav-icon" />
        }
    </button>
</div>
                       
                    </div>
                </div>

                {/* ── Mobile Menu ── */}
                <AnimatePresence>
                    {isMobileMenuOpen && (
                        <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.3 }}
                            className="mobile-nav glass-effect-dark"
                        >
                            <div className="container mobile-nav-content">
                                {navigationItems.map((navItem) => (
                                    <div key={navItem.path} className="mobile-nav-item">
                                        {navItem.children ? (
                                            <div className="mobile-dropdown">
                                                <div className={`mobile-dropdown-header ${currentLanguage === LANGUAGES.MM ? 'myanmar-text' : ''}`}>
                                                    {getText(navItem.label)}
                                                </div>
                                                <div className="mobile-dropdown-items">
                                                    {navItem.children.map((child) => (
                                                        <Link
                                                            key={child.path}
                                                            to={child.path}
                                                            className={`mobile-nav-link hover-slide ${currentLanguage === LANGUAGES.MM ? 'myanmar-text' : ''} ${location.pathname === child.path ? 'mobile-nav-link-active' : ''}`}
                                                        >
                                                            {getText(child.label)}
                                                        </Link>
                                                    ))}
                                                </div>
                                            </div>
                                        ) : (
                                            <Link
                                                to={navItem.path}
                                                className={`mobile-nav-link hover-slide ${currentLanguage === LANGUAGES.MM ? 'myanmar-text' : ''} ${isActiveNavItem(navItem) ? 'mobile-nav-link-active' : ''}`}
                                            >
                                                {getText(navItem.label)}
                                            </Link>
                                        )}
                                    </div>
                                ))}

                                {/* Mobile Language Switcher */}
                                <div className="mobile-nav-item mobile-lang-section">
                                    <div className="mobile-dropdown-header">
                                        <LanguageIcon className="mobile-lang-icon" />
                                        <span>Language / ဘာသာစကား</span>
                                    </div>
                                    <div className="mobile-lang-buttons">
                                        <button
                                            onClick={toggleLanguage}
                                            className={`mobile-lang-btn ${currentLanguage === LANGUAGES.EN ? 'mobile-lang-btn-active' : ''}`}
                                        >
                                            <span className="lang-flag">🇬🇧 </span>
                                            <span> English</span>
                                            {currentLanguage === LANGUAGES.EN && <span className="lang-check">✓</span>}
                                        </button>
                                        <button
                                            onClick={toggleLanguage}
                                            className={`mobile-lang-btn myanmar-text ${currentLanguage === LANGUAGES.MM ? 'mobile-lang-btn-active' : ''}`}
                                        >
                                            <span className="lang-flag">🇲🇲 </span>
                                            <span> မြန်မာ</span>
                                            {currentLanguage === LANGUAGES.MM && <span className="lang-check">✓</span>}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </nav>

            {/* Mobile overlay backdrop */}
            <AnimatePresence>
                {isMobileMenuOpen && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="mobile-nav-overlay fixed z-40"
                    />
                )}
            </AnimatePresence>
        </>
    );
};

export default Navbar;